import type {
  FilterSelectionMode,
  ScreenerFilter,
  ScreenerFilterOption,
} from '@api/api/screener';
import { Button } from '@design/components/button/Button';
import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import { Icon } from '@design/components/icon/Icon';
import { Input } from '@design/components/input/Input';
import { Modal } from '@design/components/modal/Modal';
import { debounce } from '@helpers/utils/debounce/debounce';
import type {
  FilterVariant,
  IncludeExcludeLabels,
} from '@terminal/components/filter/include-exclude/include-exclude.types';
import {
  getFilterOptionsBySelectionMode,
  getIncludeExcludeButtonLabels,
  INCLUDE_EXCLUDE_SELECTION_MODES,
} from '@terminal/components/filter/include-exclude/utils/include-exclude.utils';
import type { ActiveFilterOptions } from '@terminal/components/filters/Filters';
import { isExcludableSectorFilter } from '@terminal/components/filters/utils/filter-variant.utils';
import type { SetIncludeExcludeFilterOptionsProps } from '@terminal/components/update-filter-options/updateFilterOptions';
import { type ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TreeNode } from './components/TreeNode/TreeNode';
import styles from './SearchTreeModal.module.scss';
import {
  filterOptionsByHighlightedValues,
  getHighlightedOptionValues,
  getIncludeExcludeSectorOptions,
  updateSectorDraftOptions,
} from './utils';

export interface SearchTreeModalProps {
  title: string;
  activeFilter: ScreenerFilter;
  activeFilterOptions: ActiveFilterOptions[];
  includeExcludeLabels: IncludeExcludeLabels;
  variant?: FilterVariant;
  onClose: () => void;
  setFilterOptions: (filterProps: {
    filterKey: string;
    options: ScreenerFilterOption[];
  }) => void;
  setIncludeExcludeFilterOptions: (
    filterProps: SetIncludeExcludeFilterOptionsProps
  ) => void;
  resetFilterOptions: (filterKey: string) => void;
}

export const SearchTreeModal = ({
  title,
  activeFilter,
  activeFilterOptions,
  includeExcludeLabels,
  variant = 'default',
  onClose,
  setFilterOptions,
  setIncludeExcludeFilterOptions,
  resetFilterOptions,
}: SearchTreeModalProps) => {
  const { t } = useTranslation('screener');
  const isExcludableFilter =
    variant === 'include-exclude-multiselect' &&
    isExcludableSectorFilter(activeFilter);
  const filterOptionGroups = activeFilterOptions.filter(
    filterOption => filterOption.filter === activeFilter.key
  );
  const {
    includedOptions: savedIncludedOptions,
    excludedOptions: savedExcludedOptions,
  } = getFilterOptionsBySelectionMode(filterOptionGroups);
  const [selectedOptions, setSelectedOptions] =
    useState<ScreenerFilterOption[]>(savedIncludedOptions);
  const [sectorDraftOptions, setSectorDraftOptions] = useState({
    includedOptions: savedIncludedOptions,
    excludedOptions: savedExcludedOptions,
  });
  const {
    includedOptions: includedDraftOptions,
    excludedOptions: excludedDraftOptions,
  } = sectorDraftOptions;
  const [selectionMode, setSelectionMode] = useState<FilterSelectionMode>(
    savedExcludedOptions.length && !savedIncludedOptions.length
      ? 'exclude'
      : 'include'
  );
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const { options } = activeFilter.config;
  const [displayedOptions, setDisplayedOptions] =
    useState<ScreenerFilterOption[]>(options);
  const activeDraftOptions =
    selectionMode === 'include' ? includedDraftOptions : excludedDraftOptions;
  const hasSelectedOptions = isExcludableFilter
    ? includedDraftOptions.length > 0 || excludedDraftOptions.length > 0
    : selectedOptions.length > 0 || excludedDraftOptions.length > 0;
  const { includedSectorOptions, excludedSectorOptions } = isExcludableFilter
    ? getIncludeExcludeSectorOptions({
        options,
        includedOptions: includedDraftOptions,
        excludedOptions: excludedDraftOptions,
      })
    : { includedSectorOptions: [], excludedSectorOptions: [] };

  const expandHighlightedOptions = (value: string) => {
    const values = getHighlightedOptionValues({
      options,
      searchQuery: value,
    });
    const filteredOptions = filterOptionsByHighlightedValues(options, values);
    setDisplayedOptions(value ? filteredOptions : options);
    const updatedExpandedItems = [...new Set([...expandedItems, ...values])];
    setExpandedItems(updatedExpandedItems);
  };

  const debouncedHighlight = useCallback(
    debounce((value: string) => {
      expandHighlightedOptions(value);
    }, 250),
    []
  );

  useEffect(() => {
    return () => {
      debouncedHighlight.cancel();
    };
  }, [debouncedHighlight]);

  const handleCaretClick = (values: string[], isExpanded: boolean) => {
    const updatedItems = isExpanded
      ? expandedItems.filter(i => !values.includes(i))
      : [...new Set([...expandedItems, ...values])];

    setExpandedItems(updatedItems);
  };

  const handleCheckboxChange = (
    changedOptions: ScreenerFilterOption[],
    checked: boolean
  ) => {
    if (isExcludableFilter) {
      setSectorDraftOptions(currentDraftOptions =>
        updateSectorDraftOptions({
          ...currentDraftOptions,
          changedOptions,
          selectionMode,
          checked,
        })
      );

      return;
    }

    const optionsValues = changedOptions.map(o => o.value.code);
    const updatedOptions = checked
      ? [...selectedOptions, ...changedOptions]
      : selectedOptions.filter(i => !optionsValues.includes(i.value.code));

    setSelectedOptions(updatedOptions);
  };

  const resetDisplayedOptions = () => {
    debouncedHighlight.cancel();
    setSearchQuery('');
    setExpandedItems([]);
    setDisplayedOptions(options);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    debouncedHighlight(e.target.value);
  };

  const handleSubmit = () => {
    if (isExcludableFilter) {
      setIncludeExcludeFilterOptions({
        filterKey: activeFilter.key,
        includedOptions: includedDraftOptions,
        excludedOptions: excludedDraftOptions,
      });
      onClose();
      return;
    }

    if (selectedOptions.length) {
      setFilterOptions({
        filterKey: activeFilter.key,
        options: selectedOptions,
      });
    } else if (savedExcludedOptions.length) {
      setIncludeExcludeFilterOptions({
        filterKey: activeFilter.key,
        includedOptions: [],
        excludedOptions: savedExcludedOptions,
      });
    } else {
      resetFilterOptions(activeFilter.key);
    }

    onClose();
  };

  const handleSelectionModeChange = (index: number) => {
    setSelectionMode(INCLUDE_EXCLUDE_SELECTION_MODES[index] ?? 'include');
  };

  const handleClear = () => {
    resetDisplayedOptions();
    setSelectedOptions([]);
    setSectorDraftOptions({ includedOptions: [], excludedOptions: [] });
    resetFilterOptions(activeFilter.key);
  };

  return (
    <Modal
      className={styles.styledModal}
      isOpen
      title={title}
      onClose={onClose}
    >
      <div className={styles.container}>
        <Input
          className={styles.search}
          data-testid="search-tree-input"
          size="large"
          label={t('screener:gics.searchIndustry')}
          leftIcon={<Icon iconName="Search" size={20} />}
          value={searchQuery}
          onChange={handleInputChange}
          rightIcon={
            searchQuery ? (
              <div
                className={styles.clearButton}
                data-testid="clear-search-button"
                onClick={resetDisplayedOptions}
              >
                {t('screener:gics.clear')}
              </div>
            ) : null
          }
        />
        {isExcludableFilter && (
          <ButtonGroup
            labels={getIncludeExcludeButtonLabels(
              includeExcludeLabels,
              includedSectorOptions,
              excludedSectorOptions
            )}
            activeIndex={INCLUDE_EXCLUDE_SELECTION_MODES.indexOf(selectionMode)}
            onClick={handleSelectionModeChange}
          />
        )}
        <div className={styles.nodesWrapper}>
          {displayedOptions.length ? (
            displayedOptions.map(o => {
              return (
                <TreeNode
                  key={o.key}
                  option={o}
                  selectedOptions={
                    isExcludableFilter ? activeDraftOptions : selectedOptions
                  }
                  searchQuery={searchQuery}
                  expandedItems={expandedItems}
                  onClickCaret={handleCaretClick}
                  onChangeCheckBox={handleCheckboxChange}
                />
              );
            })
          ) : (
            <span className={styles.noResultsLabel}>
              {t('screener:noResults')}
            </span>
          )}
        </div>
      </div>
      <div className={styles.footerButtons}>
        {hasSelectedOptions && (
          <Button
            variant="secondary"
            size="large"
            label={t('screener:gics.clear')}
            onClick={handleClear}
          />
        )}
        <div className={styles.confirmationButtons}>
          <Button
            variant="tertiary"
            size="large"
            label={t('screener:gics.cancel')}
            onClick={onClose}
          />
          <Button
            variant="primary"
            size="large"
            label={t('screener:gics.select')}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </Modal>
  );
};
